import { graphql } from 'react-apollo';
import snakeCase from 'lodash/snakeCase';
import get from 'lodash/get';
import {
  me as meQuery,
  getQuizQuestionInfo,
  getAssessmentSecurityInfo,
  savePreference,
  getTeacherInfo,
} from 'legacy/src/queries';
import { getCourseOutline } from 'legacy/packages/unit-outline/src/queries';
import {
  FILE_UPLOAD_WIDGET_JSON,
  AUDIO_WIDGET_JSON,
} from 'legacy/src/components/learnosity/utils/constants';
import { getEducationPeriodForSubject } from 'legacy/src/components/auth/utils';
import log from 'chameleon/ui-stack/utilities/log';

export const hasNonSecureQn = 'hasNonSecureQn';

export const withQuizQuestionInfo = graphql(getQuizQuestionInfo, {
  options: ({ itemId, assignment = {} }) => ({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      // we check both the itemId and assignment object to cater for both the Assign and Manage Modals
      assessmentId: Number(itemId || get(assignment, 'item.id', '')).toString(),
      assignmentId:
        assignment && assignment.id ? Number(assignment.id).toString() : '',
      questionTypes: `${FILE_UPLOAD_WIDGET_JSON.type},${AUDIO_WIDGET_JSON.type}`,
    },
  }),
  skip: ({
    assessmentDataCache,
    itemId,
    assignment = {},
    quizHasNonSecureQns,
    isFinalExam,
    isAdminOrCoordinator,
  }) => {
    /*
     * rerun the query if we don't have both a value in the cache and directly from the BE
     * we check both the itemId and assignment object to cater for both the Assign and Manage Modals
     */
    const id = itemId || assignment.id;
    const cache = (assessmentDataCache && assessmentDataCache[id]) || {};
    return (
      isFinalExam ||
      [typeof cache[hasNonSecureQn], typeof quizHasNonSecureQns].includes(
        'boolean',
      ) ||
      isAdminOrCoordinator
    );
  },
  props: ({ data }) => {
    const { loading, quizHasNonSecureQns, error } = data;

    if (error) {
      return log('error', error);
    }

    if (loading) {
      return {
        loading,
      };
    }

    return {
      loading,
      quizHasNonSecureQns,
    };
  },
});

export const withSavePreference = graphql(savePreference, {
  name: 'savePreference',
});
export const withMeQuery = graphql(meQuery, { name: 'meQuery' });
export const withTeacherInfo = graphql(getTeacherInfo, {
  options: (props) => {
    const id = props.params.subject.toString();
    const educationPeriod = getEducationPeriodForSubject({
      subjectId: id,
      userData: props.userData,
    });
    return { variables: { id, educationPeriod } };
  },
  skip: ({ isAdminOrCoordinator }) => isAdminOrCoordinator,
  props: ({ data }) => {
    const { teacherSubject } = data;
    return {
      teacherSubject,
    };
  },
});

export const withAssessmentSecurityInfo = graphql(getAssessmentSecurityInfo, {
  options: ({ itemId, assignment }) => ({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      assessmentId: Number(itemId || get(assignment, 'item.id', '')).toString(),
    },
  }),
  skip: ({ isFinalExam, isAdminOrCoordinator }) =>
    isFinalExam || isAdminOrCoordinator,
  props: ({ data }) => {
    const { loading, assessment, error } = data;

    if (error) {
      return log('error', error);
    }

    if (loading) {
      return {
        loading,
        isAssessmentSecurityInfoLoading: loading,
      };
    }

    if (assessment?.questions?.length) {
      const { isPracticeExam } = assessment;
      // has secure questions, PE context
      const hasSecureQuestions = assessment.questions.some(
        (question) => question.isSecure,
      );

      return {
        isPracticeExam,
        quizHasAnySecureQnsPE: hasSecureQuestions,
        assessmentWithQuestionsData: assessment,
        isAssessmentSecurityInfoLoading: loading,
      };
    }

    return {
      loading,
      isAssessmentSecurityInfoLoading: loading,
    };
  },
});

export const withCourseOutline = graphql(getCourseOutline, {
  options: (props) => {
    const {
      unitsClient,
      subject,
      userData,
      cachedCourseOutlineList = {},
    } = props;
    const canSkip =
      !userData.isStudent &&
      cachedCourseOutlineList &&
      cachedCourseOutlineList[subject.id];

    return {
      client: unitsClient,
      notifyOnNetworkStatusChange: userData.isStudent,
      skip: canSkip,
      variables: {
        subjectId: subject.id,
        educationPeriod: userData.chosenEducationPeriodCode,
      },
      fetchPolicy: userData.isStudent ? 'network-only' : 'cache-first',
    };
  },
  props: ({ data: { loading, courseOutline } }) => ({
    loading,
    courseOutline,
  }),
});

/**
 * Convert first level of keys of a object from camelCase to snake case.
 *
 * Any sublevel object keys WILL NOT BE CONVERTED!
 */
export const convertKeysToSnakeCase = (obj) =>
  Object.keys(obj).reduce((acc, k) => ({ ...acc, [snakeCase(k)]: obj[k] }), {});
